<template>
  <RmField
    :field="lot.id"
  >
    <div
      class="lot-side-surface relative px-2.5 md:px-4 py-4 rounded-2xl"
    >
      <template v-if="!hasSelectedDate">
        <button
          class="flex items-center justify-between w-full py-1.5 pl-4 pr-2 text-sm font-bold text-gray-600 bg-white border-2 border-gray-300 rounded-[12px]"
          @click="handleSelectDate"
        >
          <span> Выбрать даты </span>
          <IconDropdownRound />
        </button>
      </template>
      <div
        v-else
        class="flex flex-col gap-y-4"
      >
        <div>
          <div class="text-sm font-semibold text-gray-600">Срок аренды</div>
          <div class="flex justify-between">
            <div
              v-if="!cart.isHourlyRent"
              class="text-sm font-bold text-gray-900"
            >
              c {{ $dayjs(cart.dateFrom).format('D MMMM') }} по
              {{ $dayjs(cart.dateTo).format('D MMMM') }}
            </div>
            <div
              v-else
              class="text-sm font-bold text-gray-900"
            >
              {{ $dayjs(cart.dateFrom).format('D MMMM') }}, {{ cart.rentHours }}
              {{ $t('common.hour', cart.rentHours) }}
            </div>

            <button
              class="text-sm font-medium text-blue-500 underline cursor-pointer"
              @click="handleSelectDate"
            >
              Изменить срок
            </button>
          </div>
        </div>

        <div v-if="isPriceDetailsInitial">
          <div class="text-sm font-semibold text-gray-600">Стоимость аренды</div>
          <div class="flex justify-between">
            <div class="text-sm font-bold text-gray-900">
              <template v-if="cart.isHourlyRent && lot.prices?.hourlyRentAvailable">
                {{ (priceDetails.rent / cartRentHours).toLocaleString($i18n.locale)
                }} {{ currencyChar }}
                ×
                {{ cartRentHours }} {{ $t('common.hour', cartRentHours) }}
              </template>
              <template v-else>
                {{ (priceDetails.rent / rentalDays).toLocaleString($i18n.locale)
                }} {{ currencyChar }}
                × {{ rentalDays }} {{ $t('common.day', rentalDays) }}
              </template>
            </div>
            <div class="whitespace-nowrap font-bold text-green-500 text-sm">
              {{ priceDetails.rent.toLocaleString($i18n.locale) }} {{ currencyChar }}
            </div>
          </div>
        </div>
        <CalculatorInsuranceToggle
          v-if="isPriceDetailsInitial && lot.category?.insurance"
          :insurance-enabled="insuranceToggleValue"
          :insurance-price="priceDetails?.insurancePrice || 0"
          :insurance-days="insuranceDays"
          :insurance-total-price="insuranceTotalPrice"
          :currency-char="currencyChar"
          @insurance-info="handleInsuranceInfo"
          @insurance-change="handleChangeInsurance"
        />
      </div>

      <PolkaAlert
        v-if="isPriceDetailsInitial && !lot.prices?.hourlyRentAvailable && cart.isHourlyRent"
        class="my-4"
        title="Почасовая аренда недоступна"
        description="Товар будет добавлен по суточному тарифу"
      />

      <div
        v-if="isPriceDetailsInitial && rentalDays < lot.minRentDays"
        class="bg-orange-50 p-4 flex items-center gap-4 rounded-2xl my-4"
      >
        <IconInfoCircle class="text-orange-250" />
        <div>
          <div class="text-sm font-bold text-black">
            {{ $t('lots.id.action.minimalDays', lot.minRentDays) }}
          </div>
        </div>
      </div>

      <CalculatorActions
        ref="calculatorActionsRef"
        :lot-id="lotId"
        :insurance-selected="insuranceSelected"
      />

      <div class="mx-auto mt-2 text-center">
        <span class="text-gray-600 text-sm pr-1"> Наличие: </span>
        <span class="text-green-500 font-bold text-sm"> {{ lot.quantity }} шт. </span>
      </div>
    </div>
  </RmField>
</template>

<script>
import debounce from 'es-toolkit/compat/debounce'
import { nextTick, onMounted } from 'vue'
import { buildAnalyticsStore } from '~/analytics/composables-types'
import IconDropdownRound from '~/assets/svg/lot-page/dropdown-round.svg?component'
import IconInfoCircle from '~/assets/svg/ui/info-circle.svg?component'
import CalculatorActions from '~/components/lots/show/calculator_actions.vue'
import CalculatorInsuranceToggle from '~/components/lots/show/calculator_insurance_toggle.vue'
import PolkaAlert from '~/components/shared/polka_alert.vue'
import RmField from '~/components/shared/rm_field.vue'
import { useLot } from '~/composables/useLot'
import { getToggleInsuranceProductNotInCartAmplitudePayload } from '~/composables/useToggleInsuranceAnalytics'
import { useView } from '~/composables/useView.js'
import { getViewInsuranceInfoAmplitudePayload } from '~/composables/useViewInsuranceInfoAnalytics'

export default {
  components: {
    IconDropdownRound,
    RmField,
    CalculatorActions,
    CalculatorInsuranceToggle,
    IconInfoCircle,
    PolkaAlert,
  },
  props: {
    lotId: {
      type: [String, Number],
      required: true,
    },
  },
  setup (props) {
    const { lot, currency, isLoading, isPriceDetailsInitial, calculatePrice, priceDetails } = useLot(props.lotId)
    const cartState = useCart()
    const globalState = useGlobal()
    const calculatorActionsRef = ref(null)
    const showBottomPanelInLot = globalState.showBottomPanelInLot
    const insuranceSelected = ref(true)
    const getLotById = computed(() => cartState.getLotById(lot.value?.id))

    const checkElementVisibility = () => {
      if (!calculatorActionsRef.value || !import.meta.client || typeof window === 'undefined') return

      const el = calculatorActionsRef.value.$el || calculatorActionsRef.value
      if (!el || !el.getBoundingClientRect) return

      const rect = el.getBoundingClientRect()
      const viewportHeight = globalThis.innerHeight


      const isVisible = rect.top < viewportHeight && rect.bottom > 0

      showBottomPanelInLot.value = !isVisible
    }

    onMounted(() => {
      nextTick(() => {
        checkElementVisibility()
      })
    })

    useView(calculatorActionsRef, {
      handler: (e) => {
        if (
          e.percentCenter === 0 &&
          e.percentInView === 0 &&
          e.percentTop === 0 &&
          e.scrollPercent === 0
        ) {
          showBottomPanelInLot.value = false
          return
        }

        if (e.percentInView < 0.8) {
          showBottomPanelInLot.value = true
        } else {
          showBottomPanelInLot.value = false
        }
      },
    })

    return {
      calculatorActionsRef,
      isPriceDetailsInitial,
      showBottomPanelInLot,
      insuranceSelected,
      getLotById,
      cart: cartState.item,
      lot,
      currency,
      isLoading,
      priceDetails,
      rentalDays: cartState.rentalDays,
      cartRentHours: cartState.rentHours,
      calculatePrice,
      cartUpdateDates: cartState.cartUpdateDates,
      cartUpdateLotQuantity: cartState.cartUpdateLotQuantity,
      cartAddLot: cartState.cartAddLot,
      cartUpdateLotInsurance: cartState.cartUpdateLotInsurance,
    }
  },

  computed: {
    hasSelectedDate () {
      return this.cart?.dateFrom && this.cart?.dateTo
    },
    deposit () {
      return this.priceDetails.deposit
    },
    deliveryPrice () {
      return this.lot.deliveryPrice
    },
    deliveryTitle () {
      return this.$t('lots.id.deliveryReturns') + ': ' + this.deliveryPrice + this.currencyChar
    },
    currencyChar () {
      if (this.currency === 'RUB') {
        return '₽'
      }
      return '$'
    },
    insuranceDays () {
      return Math.max(this.rentalDays || 0, 1)
    },
    insuranceTotalPrice () {
      return this.priceDetails?.insurancePrice * this.insuranceDays
    },
    insuranceInCartSelected () {
      return Boolean(this.getLotById?.priceDetails?.insurance)
    },
    insuranceToggleValue () {
      if (this.getLotById?.quantity) {
        return this.insuranceInCartSelected
      }
      return this.insuranceSelected
    },
  },
  watch: {
    cart: {
      handler () {
        this.debouncedUpdatePrice()
        if (!this.rentalDays && !this.cartRentHours) return
      },
      immediate: true,
    },
  },
  methods: {
    handleInsuranceInfo () {
      this.$tracking.logEventAmplitude(
        'view insurance info',
        getViewInsuranceInfoAmplitudePayload({
          store: buildAnalyticsStore(),
          lot: this.lot,
          sourceBlockType: 'INSURANCE_SWITCH',
          pageKind: 'PRODUCT',
        }),
      )
      this.$modal.show('lots/show/insurance_details_modal', {
        noPadding: true,
        desktopLikeMobile: true,
        maxWidthClass: 'max-w-[375px]',
        minWidthClass: 'min-w-[275px]',
      })
    },
    debouncedUpdatePrice: debounce(function () {
      this.updatePrice()
    }, 150),
    updatePrice () {
      if (!this.cart || !this.cart.dateFrom || !this.cart.dateTo) {
        return
      }

      const dateFrom = this.$dayjs(this.cart.dateFrom)
      const dateTo = this.$dayjs(this.cart.dateTo)

      // Проверяем, что dayjs может корректно обработать даты
      if (!dateFrom.isValid() || !dateTo.isValid()) {
        return
      }

      this.calculatePrice({
        lotId: this.lot.id,
        dateFrom: new Date(dateFrom.format('YYYY-MM-DD')).toISOString(),
        dateTo: new Date(dateTo.format('YYYY-MM-DD')).toISOString(),
        deliveryFlag: false,
        insuranceFlag: this.lot.category?.insurance ? this.insuranceToggleValue : false,
        rentHours: this.lot.prices?.hourlyRentAvailable ? this.cartRentHours : null,
      })
    },

    handleSelectDate () {
      this.$modal.show('lots/show/date_select', {
        noPadding: true,
        minRentDays: this.lot.minRentDays,
        dates: {
          start: this.cart?.dateFrom,
          end: this.cart?.dateTo,
        },
        id: this.lot.id,
        confirmationMethod: this.confirmationDate,
        hourlyRentPrices: this.lot.hourlyRentPrices,
        hourlyRentAvailable: this.lot.prices?.hourlyRentAvailable,
        prices: this.lot.prices,
        isHourlyRent: this.cart?.isHourlyRent,
        cartRentHours: this.cart?.rentHours || 1,
        priceDetails: this.priceDetails,
        hasInCart: this.getLotById.quantity > 0,
        equipmentAndVehicles: Boolean(this.lot?.category?.equipmentAndVehicles),
      })
    },
    async confirmationDate (range, rentHours) {
      const dateFrom = range.start instanceof Date
        ? range.start.toISOString()
        : this.$dayjs(range.start).toISOString()
      const dateTo = range.end instanceof Date
        ? range.end.toISOString()
        : this.$dayjs(range.end).toISOString()
      await this.cartUpdateDates({
        dateFrom,
        dateTo,
        source: 'lot',
        lotId: this.lot.id,
        lot: this.lot,
        rentHours,
      })
      const willAdd = !this.getLotById.quantity
      if (willAdd) {
        await this.cartAddLot({
          lotId: this.lot.id,
          insurance: this.wantInsuranceOnCartAdd(),
        })
      }
    },
    wantInsuranceOnCartAdd () {
      return Boolean(this.lot?.category?.insurance) && this.insuranceSelected === true
    },
    handleChangeInsurance ({ value }) {
      this.insuranceSelected = value
      // this.updatePrice()
      if (!this.cart?.id || !this.lot?.id || !this.getLotById?.quantity) {
        if (this.cart?.id && this.lot?.id) {
          this.$tracking.logEventAmplitude(
            'toggle insurance',
            getToggleInsuranceProductNotInCartAmplitudePayload({
              store: buildAnalyticsStore(),
              lot: this.lot,
              cart: this.cart,
              insuranceOn: value,
              priceDetails: this.priceDetails,
              rentalDays: this.rentalDays,
            }),
          )
        }
        return
      }
      this.cartUpdateLotInsurance({
        lotId: this.lot.id,
        insurance: value,
      })
    },
  },
}
</script>
